/** 业务统计图表色（唯一 JS 配色源）
 * accent：折线/柱状主色，跟 REACT_APP_THEME_PRIMARY 派生；对比年分色用 yearLines 辅色板。
 * 页面样式 token 见 assets/scss/theme/_statistics-tokens.scss
 */
import { getThemePrimaryForMode, mixHex } from '../../../../config/themePalette';

const DARK_YEAR_LINE_COLORS = ['#6366f1', '#38bdf8', '#a78bfa', '#34d399', '#f59e0b'];
const DARK_ANCHOR_COLOR = '#6366f1';
const DARK_COMPARE_POINT_COLOR = '#38bdf8';
const DARK_YEAR_COMPARE_LINE_COLORS = ['#38bdf8', '#a78bfa', '#34d399', '#f59e0b', '#fb7185'];

const buildBrandSeriesColors = (primary, isLight) => {
	const accentLight = mixHex(primary, '#ffffff', isLight ? 0.22 : 0.28);
	const accentDeep = mixHex(primary, '#000000', isLight ? 0.12 : 0.08);
	return [
		primary,
		accentLight,
		accentDeep,
		mixHex(primary, '#ffffff', isLight ? 0.42 : 0.38),
		mixHex(primary, '#000000', isLight ? 0.22 : 0.18),
		mixHex(primary, '#ffffff', isLight ? 0.58 : 0.52),
	];
};

/** 统计图表轴/地图/tooltip 主色 */
export function getStatisticsChartPrimary(isLight) {
	return getThemePrimaryForMode(isLight);
}

/** 折线对比、自定义图等 UI accent */
export function getStatisticsAccentColor(isLight, getThemeColor) {
	return getThemeColor?.('primary') || getThemePrimaryForMode(isLight);
}

export function resolveStatisticsYearLineColors(isLight) {
	if (!isLight) return DARK_YEAR_LINE_COLORS;
	return buildBrandSeriesColors(getThemePrimaryForMode(true), true);
}

export function resolveTrendChartPalette(isLight) {
	if (!isLight) {
		return {
			anchor: DARK_ANCHOR_COLOR,
			comparePoint: DARK_COMPARE_POINT_COLOR,
			yearLines: DARK_YEAR_COMPARE_LINE_COLORS,
		};
	}

	const primary = getThemePrimaryForMode(true);
	const accentLight = mixHex(primary, '#ffffff', 0.22);
	return {
		anchor: primary,
		comparePoint: accentLight,
		yearLines: buildBrandSeriesColors(primary, true),
	};
}

const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** 规范同比年自定义色：仅保留合法 hex，键为年份字符串 */
export function normalizeTrendYearSeriesColors(colors = {}) {
	if (!colors || typeof colors !== 'object') return {};
	const next = {};
	for (const [rawYear, rawColor] of Object.entries(colors)) {
		const year = Number(rawYear);
		if (!Number.isFinite(year) || year < 2000 || year > 2100) continue;
		const color = String(rawColor ?? '').trim();
		if (!HEX_COLOR_RE.test(color)) continue;
		next[String(year)] = color.length === 4
			? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
			: color.toLowerCase();
	}
	return next;
}

/** 同比年系列色：有自定义用自定义，否则基准用 accent、对比年走 yearLines */
export function resolveTrendYearSeriesColor({
	year,
	isAnchor = false,
	colors = {},
	accentColor = null,
	isLight = true,
	fallbackIndex = 0,
} = {}) {
	const yearNum = Number(year);
	const custom = normalizeTrendYearSeriesColors(colors);
	if (Number.isFinite(yearNum) && custom[String(yearNum)]) {
		return custom[String(yearNum)];
	}
	const palette = resolveTrendChartPalette(isLight);
	if (isAnchor) return accentColor || palette.anchor;
	const yearLines = palette.yearLines ?? [];
	if (!yearLines.length) return accentColor || palette.anchor;
	return yearLines[Math.max(0, fallbackIndex) % yearLines.length];
}

/** 未自定义时的默认展示色（供选色控件预填） */
export function resolveTrendYearSeriesDefaultColor({
	year,
	isAnchor = false,
	accentColor = null,
	isLight = true,
	fallbackIndex = 0,
} = {}) {
	return resolveTrendYearSeriesColor({
		year,
		isAnchor,
		colors: {},
		accentColor,
		isLight,
		fallbackIndex,
	});
}

/** 制作区同比年可选色板（固定 swatch，不做自由取色） */
export function resolveTrendYearSeriesSwatchColors(isLight = true, accentColor = null) {
	const palette = resolveTrendChartPalette(isLight);
	const primary = (accentColor || palette.anchor || '').toLowerCase();
	const extras = isLight
		? ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899']
		: ['#fb7185', '#fbbf24', '#34d399', '#60a5fa', '#c084fc', '#f472b6'];
	const candidates = [primary, ...(palette.yearLines ?? []), ...extras]
		.map((color) => String(color ?? '').trim().toLowerCase())
		.filter((color) => HEX_COLOR_RE.test(color));
	const seen = new Set();
	const swatches = [];
	for (const color of candidates) {
		const normalized =
			color.length === 4 ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}` : color;
		if (seen.has(normalized)) continue;
		seen.add(normalized);
		swatches.push(normalized);
		if (swatches.length >= 10) break;
	}
	return swatches;
}

/** 时段对比轴点年份（下单日起始） */
export function resolveTrendComparePointYear(point = {}) {
	const raw = point?.startDate ?? point?.periodStartDate ?? '';
	const year = Number(String(raw).slice(0, 4));
	return Number.isFinite(year) ? year : null;
}

/**
 * 时段对比柱/点颜色：优先按年份自定义色；无年份时回退基准/对比默认色。
 * @param {object} point timeline 点
 * @param {object} options
 * @param {number} [options.compareIndex] 非基准点在对比序列中的序号（用于默认辅色板）
 */
export function resolvePeriodComparePointColor(
	point = {},
	{ yearSeriesColors = {}, accentColor = null, isLight = true, fallbackCompareColor = null, compareIndex = 0 } = {},
) {
	const isAnchor = point?.kind === 'anchor';
	const year = resolveTrendComparePointYear(point);
	if (Number.isFinite(year)) {
		return resolveTrendYearSeriesColor({
			year,
			isAnchor,
			colors: yearSeriesColors,
			accentColor,
			isLight,
			fallbackIndex: isAnchor ? 0 : compareIndex,
		});
	}
	const palette = resolveTrendChartPalette(isLight);
	if (isAnchor) return accentColor || palette.anchor;
	return fallbackCompareColor || palette.comparePoint;
}
